import { DeleteOutlined, EyeOutlined, FileExclamationOutlined, MenuOutlined, PlayCircleOutlined, StopOutlined } from "@ant-design/icons";
import { Button, Dropdown, Pagination, Tooltip, Typography } from "antd";
import BadgeIndicator from "components/atoms/BadgeIndicator";
import dayjs from "dayjs";
import React from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";
import getElapsedTime from "../DuplicateDataObjectJobMetaData/getElapsedTime";

interface IDuplicateDataObjectJobClusteredViewProps {
    jobList: IDataObjectsDuplicateJob[];
    onViewData: (j: IDataObjectsDuplicateJob) => void;
    onDeleteJob: (j: IDataObjectsDuplicateJob) => void;
    onCancelJob: (id: string, version: string) => void;
    onRestartJob: (j: IDataObjectsDuplicateJob) => void;
}

export default function DuplicateDataObjectJobClusteredView({ jobList, onViewData, onDeleteJob, onCancelJob, onRestartJob }: IDuplicateDataObjectJobClusteredViewProps) {
    const [paginatedList, setPaginatedList] = React.useState<IDataObjectsDuplicateJob[]>([]);
    const [pagination, setPagination] = React.useState<IPagination>({
        current: 1,
        pageSize: 9, // In threes
        page: 1,
    });

    const copyList = React.useMemo(
        () =>
            Array.from(jobList).filter((job) => {
                if (["RUNNING", "CREATED", "PENDING", "CANCELLED"].includes(job.status)) {
                    // Show running or created regardless of result
                    return true;
                }

                if (["COMPLETED"].includes(job.status) && !!job.runInfo) {
                    // For completed jobs, only show those with duplication
                    // i.e. total records not equal to total clusters
                    return job.runInfo.totalClusters !== job.runInfo.totalRecords;
                }

                return false;
            }),
        [jobList],
    );

    const getBadgeColor = React.useCallback((status: string): "red" | "green" | "yellow" | undefined => {
        switch (status) {
            case "RUNNING":
                return "yellow";
            case "COMPLETED":
                return "green";
            case "CANCELLED":
                return "red";
            default:
                return undefined;
        }
    }, []);

    React.useEffect(() => {
        if (copyList) {
            setPaginatedList(copyList.slice((pagination.current - 1) * pagination.pageSize, pagination.current * pagination.pageSize));
        }
    }, [copyList, pagination]);

    return (
        <div data-testid={CypressTestIds.DUPLICATE_SEARCH_CLUSTERED_VIEW_CONTAINER} className="flex w-full flex-1 flex-col gap-3 overflow-hidden">
            {copyList && copyList.length > 0 ? (
                <>
                    <div className="scroll-webkit flex w-full flex-1 flex-col gap-3 overflow-auto pr-1">
                        <div className="grid w-full flex-1 grid-cols-1 gap-3 md:grid-cols-3">
                            {paginatedList.map((job) => (
                                <div
                                    key={job.jobId}
                                    onClick={() => job.status === "COMPLETED" && onViewData(job)}
                                    className="cluster-card view-cluster-btn flex max-h-[300px] cursor-pointer flex-col justify-between gap-1 rounded-xl border border-gray-200 bg-white p-6 shadow-md transition-shadow duration-300 hover:bg-slate-100 hover:shadow-lg"
                                >
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-start justify-between gap-4">
                                            <h3 className="text-xl font-bold leading-none text-gray-800">{job.name}</h3>
                                            {["COMPLETED", "CANCELLED"].includes(job.status) && (
                                                <Dropdown
                                                    destroyOnHidden
                                                    menu={{
                                                        items: [
                                                            job.status === "COMPLETED"
                                                                ? {
                                                                      key: "option-view-job",
                                                                      label: "View",
                                                                      onClick: (e) => {
                                                                          e.domEvent && e.domEvent.stopPropagation && e.domEvent.stopPropagation();
                                                                          onViewData(job);
                                                                      },
                                                                      icon: <EyeOutlined />,
                                                                      ...{ "data-testid": CypressTestIds.DUPLICATE_SEARCH_CONFIG_LIST_VIEW_DATA_BUTTON },
                                                                  }
                                                                : null,
                                                            {
                                                                key: "option-re-run-job",
                                                                label: "Run Again",
                                                                onClick: (e) => {
                                                                    e.domEvent && e.domEvent.stopPropagation && e.domEvent.stopPropagation();
                                                                    onRestartJob(job);
                                                                },
                                                                icon: <PlayCircleOutlined />,
                                                                ...{ "data-testid": CypressTestIds.DUPLICATE_SEARCH_CONFIG_LIST_RUN_JOB_AGAIN_BUTTON },
                                                            },
                                                            {
                                                                type: "divider",
                                                                key: "option-divider",
                                                            },
                                                            {
                                                                key: "option-delete-job",
                                                                label: "Delete",
                                                                onClick: (e) => {
                                                                    e.domEvent && e.domEvent.stopPropagation && e.domEvent.stopPropagation();
                                                                    onDeleteJob(job);
                                                                },
                                                                icon: <DeleteOutlined />,
                                                                danger: true,
                                                                ...{ "data-testid": CypressTestIds.DUPLICATE_SEARCH_CONFIG_LIST_DELETE_BUTTON },
                                                            },
                                                        ],
                                                    }}
                                                    placement="bottomRight"
                                                    trigger={["click"]}
                                                >
                                                    <Button
                                                        data-testid={CypressTestIds.DUPLICATE_SEARCH_CLUSTERED_VIEW_DROPDOWN}
                                                        icon={<MenuOutlined />}
                                                        shape="circle"
                                                        type="primary"
                                                        size="small"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </Dropdown>
                                            )}
                                            {["RUNNING"].includes(job.status) && (
                                                <Tooltip placement="bottomRight" title="Stop running">
                                                    <Button
                                                        icon={<StopOutlined />}
                                                        shape="circle"
                                                        danger
                                                        type="primary"
                                                        size="small"
                                                        data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_LIST_CANCEL_JOB_BUTTON}
                                                        onClick={(e) => {
                                                            e.stopPropagation && e.stopPropagation();
                                                            onCancelJob(job.jobId, job.version);
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                        {job.description && <p>{job.description}</p>}
                                        <div className="flex flex-col gap-1 text-sm">
                                            <div>
                                                <span className="font-bold">Started</span>: {dayjs(job.startedDate).format("DD/MM/YYYY HH:mm:ss")}
                                            </div>
                                            <div>
                                                <span className="font-bold">Completed</span>: {dayjs(job.completedDate).format("DD/MM/YYYY HH:mm:ss")}
                                            </div>
                                            <div>
                                                <span className="font-bold">Elapsed</span>: {getElapsedTime(job)}
                                            </div>
                                            <div>
                                                <span className="font-bold">By</span>: {job.createdByEmail}
                                            </div>
                                        </div>
                                        {job.runInfo?.status && <p className="text-sm text-gray-600">{job.runInfo.status}</p>}
                                    </div>
                                    <div className="mt-2 flex items-center justify-between">
                                        {job.runInfo?.totalRecords && <div className="flex items-center text-lg font-bold text-blue-600">{job.runInfo.totalRecords} Records</div>}
                                        <BadgeIndicator className="cluster-card-status" color={getBadgeColor(job.status)}>
                                            {job.status}
                                        </BadgeIndicator>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="empty-separator h-12 w-full"></div>
                    </div>
                    <div className="flex justify-end overflow-hidden">
                        <Pagination
                            className="data-table-pagination gap-2"
                            size="small"
                            defaultCurrent={1}
                            current={pagination.current}
                            total={copyList.length}
                            pageSize={pagination.pageSize}
                            showTotal={(total, range) => (
                                <div>
                                    {range[0]} - {range[1]} of total {total}
                                </div>
                            )}
                            showSizeChanger={false}
                            showQuickJumper={false}
                            onChange={(page, pageSize) => setPagination((prev) => ({ ...prev, current: page, pageSize: pageSize }))}
                        />
                    </div>
                </>
            ) : (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-md bg-gray-50 text-gray-400">
                    <FileExclamationOutlined className="text-4xl" />
                    <Typography className="text-gray-400">No data</Typography>
                </div>
            )}
        </div>
    );
}
